import { Wifi, WifiOff, X as XIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

// Status bars above the mail list for the offline mode (#785, mockup 1d
// `.offbar`): neutral card, warning colour only on the icon; the green variant
// reports what went out after the connection came back.

/** HH:MM in German local time. */
export function formatClock(value: number | string): string {
  return new Date(value).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

function mails(n: number): string {
  return `${n} ${n === 1 ? 'Mail' : 'Mails'}`
}

/** „Wieder online · 1 Mail gesendet, 3 neue geladen" (second part only when known). */
export function onlineAgainText(sent: number, loaded: number | null): string {
  const tail = loaded === null ? '' : `, ${loaded} neue geladen`
  return `${mails(sent)} gesendet${tail}`
}

const BAR = 'flex items-center gap-2 rounded-[10px] border px-3 py-2 text-xs text-text-secondary'

/** Offline: stored state with its time, or (protected / nothing stored) only
 * „keine Verbindung zum Server". */
export function OfflineBar({ savedAt }: { savedAt: number | null }) {
  return (
    <div role="status" className={cn(BAR, 'border-border-default bg-bg-overlay')}>
      <WifiOff className="h-[15px] w-[15px] shrink-0 text-warning" aria-hidden="true" />
      <div className="min-w-0 flex-1 leading-snug">
        {savedAt === null ? (
          <p>
            <b className="font-medium text-text-primary">Offline</b> · keine Verbindung zum Server
          </p>
        ) : (
          <>
            <p>
              <b className="font-medium text-text-primary">Offline</b> · gespeicherter Stand von{' '}
              {formatClock(savedAt)}
            </p>
            <p>Letzte 50 Unterhaltungen · Anhänge nur, wenn schon geöffnet</p>
          </>
        )}
      </div>
    </div>
  )
}

/** Green bar after the outbox went out; the page hides it after 5 s. */
export function OnlineAgainBar({
  sent,
  loaded,
  onClose,
}: {
  sent: number
  loaded: number | null
  onClose: () => void
}) {
  return (
    <div role="status" className={cn(BAR, 'border-success/25 bg-success/10 py-0 pr-0')}>
      <Wifi className="h-[15px] w-[15px] shrink-0 text-success" aria-hidden="true" />
      <p className="min-w-0 flex-1 py-2 leading-snug">
        <b className="font-medium text-text-primary">Wieder online</b> ·{' '}
        {onlineAgainText(sent, loaded)}
      </p>
      <button
        type="button"
        onClick={onClose}
        aria-label="Hinweis schließen"
        className="flex h-11 w-11 shrink-0 items-center justify-center text-text-tertiary hover:text-text-primary"
      >
        <XIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  )
}

/** Foot hint under the offline list. */
export function OfflineFootHint() {
  return (
    <p className="px-4 py-3 text-center text-[11px] leading-relaxed text-text-tertiary">
      Lesen, Antworten und Schreiben gehen weiter. Löschen, Verschieben und Suche auf dem Server
      erst wieder online.
    </p>
  )
}
